"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AlertHandle = void 0;
/**
 * Describes a handle to a contextually rendered alert, providing a mechanism to update
 * message (children) and other properties as well as to dismiss the alert.
 * @class
 */
var AlertHandle = /** @class */ (function () {
    /**
     * Handle to a contextually rendered Alert.
     * @param message the children prop of the referenced alert
     * @param props the properties (minus children) of the referenced alert
     * @param onUpdate a callback function which is called whenever the alert's state changes
     */
    function AlertHandle(message, props, onUpdate) {
        var _this = this;
        this._isDismissed = false;
        this._message = "";
        this._props = {};
        /**
         * Update the alert message if it has not been dismissed (otherwise a no-op)
         * @param m the React.ReactNode message to replace the existing one
         */
        this.updateMessage = function (m) {
            var _a;
            if (_this._isDismissed) {
                return;
            }
            _this._message = m;
            (_a = _this._onUpdate) === null || _a === void 0 ? void 0 : _a.call(_this);
        };
        /**
         * Update the properties of the referenced alert, minus the children (message) if it has not
         * been dismissed (otherwise a no-op)
         * @param p a partial model of IGKAlertProps to merge into the existing props
         */
        this.updateProps = function (p) {
            var _a;
            if (_this._isDismissed) {
                return;
            }
            // make sure that we have a handle to the onExited method to detect/fire dismissals.
            // need to be cautious not to end up in an infinite loop here...
            var _b = _this.props, oldOnExited = _b.onExited, oldProps = __rest(_b, ["onExited"]);
            var newOnExited = p.onExited, newProps = __rest(p, ["onExited"]);
            var dismissMe = function () {
                var _a;
                _this._isDismissed = true;
                (_a = _this._onUpdate) === null || _a === void 0 ? void 0 : _a.call(_this);
            };
            // If incoming prop, extend it. Otherwise, use the existing prop or define it if it doesn't exist.
            var onExited = newOnExited
                ? function () {
                    dismissMe();
                    newOnExited();
                }
                : oldOnExited !== null && oldOnExited !== void 0 ? oldOnExited : dismissMe;
            // Merge the props, fire notification (if applicable)
            _this._props = __assign(__assign(__assign({}, oldProps), newProps), { onExited: onExited });
            (_a = _this._onUpdate) === null || _a === void 0 ? void 0 : _a.call(_this);
        };
        /**
         * Dismiss this alert and cause all further updates to be no-ops.
         *
         * @remarks
         * This closes the alert by setting `isOpen=false` and then actually dismisses (sets property
         * `isDismissed=true`) once the exit animation is completed.
         */
        this.dismiss = function () {
            if (!_this._isDismissed) {
                _this.updateProps({ isOpen: false });
            }
        };
        this.reset = function (message, props, onUpdate) {
            var tmpOnUpdate = onUpdate !== null && onUpdate !== void 0 ? onUpdate : _this._onUpdate;
            _this._isDismissed = false;
            _this._onUpdate = undefined;
            _this.updateMessage(message);
            _this.updateProps(props);
            _this._onUpdate = tmpOnUpdate;
        };
        this.reset(message, props, onUpdate);
    }
    Object.defineProperty(AlertHandle.prototype, "isDismissed", {
        /** Whether the alert has been dismissed or not. If it has, all other setters will be a no-op. */
        get: function () {
            return this._isDismissed;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AlertHandle.prototype, "message", {
        /** Get the current message, or the last message before the alert was dismissed. */
        get: function () {
            return this._message;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AlertHandle.prototype, "props", {
        /** Get the current props of the referenced alert, minus the children (message) */
        get: function () {
            return this._props;
        },
        enumerable: false,
        configurable: true
    });
    return AlertHandle;
}());
exports.AlertHandle = AlertHandle;
exports.default = AlertHandle;
